import React, { useMemo } from "react";
import { Avatar } from "../shared/Avatar";
import { CustomIcon } from "../shared/CustomIcon";
import { formatDate } from "../../lib/format";
import { buildCSV, downloadCSV } from "./_utils";
import { useAppSettings } from "../../lib/AppSettingsContext";

// ============================================================
// Follow-Up Team tab — per-team-member / per-pastor activity stats.
// Shows total FUs, last activity date, assigned members count, status.
// Inactive threshold = per-user override OR app-wide default (settings).
// ============================================================
const TRACKED_ROLES = new Set(["team_member", "pastor", "limited_admin", "superadmin"]);

export default function FollowupTeamTab({ users, followups }) {
  const { settings: appSettings } = useAppSettings();
  const defaultDays = Math.max(1, Number(appSettings.defaultInactivityDays) || 30);
  const rows = useMemo(() => {
    const list = (users || []).filter(u => TRACKED_ROLES.has(u.role));
    const now = Date.now();
    return list.map(u => {
      const myFus = (followups || []).filter(f => f.teamMemberId === u.id);
      const last = myFus.slice().sort((a, b) => new Date(b.date) - new Date(a.date))[0];
      const lastTs = last ? new Date(last.date).getTime() : null;
      const daysSince = lastTs !== null ? Math.floor((now - lastTs) / (24 * 3600 * 1000)) : null;
      const threshold = (typeof u.inactiveAfter === "number" && u.inactiveAfter > 0) ? u.inactiveAfter : defaultDays;
      const inactive = lastTs === null || daysSince > threshold;
      return {
        id: u.id,
        name: u.name,
        role: u.role,
        photo: u.photo,
        assigned: (u.assignedMembers || []).length,
        total: myFus.length,
        lastActivity: last ? last.date : null,
        daysSince,
        threshold,
        inactive,
      };
    }).sort((a, b) => {
      if (a.inactive !== b.inactive) return a.inactive ? 1 : -1;
      return b.total - a.total;
    });
  }, [users, followups, defaultDays]);

  return (
    <div data-testid="analytics-followup-team-tab">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          data-testid="fut-export-csv"
          onClick={() => {
            const headers = ["Name", "Role", "Assigned Members", "Total Follow-ups", "Last Activity", "Days Since", "Status"];
            const csvRows = rows.map(r => [r.name, r.role, r.assigned, r.total, r.lastActivity || "", r.daysSince ?? "", r.inactive ? "Inactive" : "Active"]);
            downloadCSV("followup-team.csv", buildCSV({ headers, rows: csvRows }));
          }}
          disabled={rows.length === 0}
        >⬇ CSV</button>
      </div>
      <div className="fut-stats-bar">
        <div className="fut-stat"><span className="fut-stat-num">{rows.length}</span><span className="fut-stat-label">Tracked staff</span></div>
        <div className="fut-stat"><span className="fut-stat-num">{rows.filter(r => !r.inactive).length}</span><span className="fut-stat-label">Active</span></div>
        <div className="fut-stat fut-stat-warn"><span className="fut-stat-num">{rows.filter(r => r.inactive).length}</span><span className="fut-stat-label">Inactive (&gt;{defaultDays}d default)</span></div>
      </div>

      {rows.length === 0 ? (
        <div className="analytics-empty">No team members or pastors yet.</div>
      ) : (
        <div className="fut-list" data-testid="fut-list">
          {rows.map(r => (
            <div key={r.id} className="fut-row" data-testid={`fut-row-${r.id}`}>
              <Avatar name={r.name} photo={r.photo} memberId={r.memberId} style={{ width: 38, height: 38, fontSize: 12 }} />
              <div className="fut-row-main">
                <div className="fut-row-name">{r.name}</div>
                <div className="fut-row-role">{roleLabel(r.role)}</div>
              </div>
              <div className="fut-row-stats">
                <Stat label="Follow-ups" value={r.total} testid={`fut-fu-${r.id}`} />
                <Stat label="Assigned" value={r.assigned} testid={`fut-asg-${r.id}`} />
                <Stat label="Last activity" value={r.lastActivity ? formatDate(r.lastActivity) : "Never"} testid={`fut-last-${r.id}`} smallValue />
              </div>
              <span
                data-testid={`fut-status-${r.id}`}
                className={`fut-badge ${r.inactive ? "is-inactive" : "is-active"}`}
                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                {r.inactive
                  ? <><CustomIcon name="warning" size={11} /> Inactive{r.daysSince !== null ? ` · ${r.daysSince}d` : ""}</>
                  : <><CustomIcon name="check" size={11} /> Active</>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid, smallValue }) {
  return (
    <div className="fut-stat-cell">
      <div className={`fut-stat-cell-value ${smallValue ? "small" : ""}`} data-testid={testid}>{value}</div>
      <div className="fut-stat-cell-label">{label}</div>
    </div>
  );
}

function roleLabel(r) {
  if (r === "superadmin") return "Super Admin";
  if (r === "limited_admin") return "Limited Admin";
  if (r === "pastor") return "Pastor";
  if (r === "team_member") return "Team Member";
  return r;
}
